import { Grid, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { validateDate } from "../../../../app/common";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { TextFieldLight } from "../../../../common/components/styled/styled";
import isEmpty from "../../../../common/helpers/isEmpty";
import { VerticalAlignGrid } from "../../../invoices/styled";
import { CustomerContract } from "../../customerAPI";
import { updateContractPricing } from "../../customerContractsSlice";
import { formatDateForBE } from "../helpers/commonUtils";
import {
  buildSpecialPricingTableDataArray,
  buildTesPricingTableDataArray,
  buildUpdatedPricingObjectForApi,
  validatePricingGeneralSettings,
} from "../helpers/pricingUtils";
import PricingEditModal from "./form/common/PricingEditModal";
import PricingTablesSection from "./form/common/PricingTablesSection";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";

interface Props {
  contractData: CustomerContract;
  eezyCompanyName: string;
  closeModal: () => void;
}

const NewPricingNoExistingDataForm = ({
  contractData,
  eezyCompanyName,
  closeModal,
}: Props) => {
  const { t } = useTranslation("customers");
  const dispatch = useAppDispatch();
  const { tes } = useAppSelector((state) => state.localized);
  const { status } = useAppSelector((state) => state.customerContracts);
  const isLoading = status === "pending";
  const [modalState, setModalState] = useState<{
    show: boolean;
    selectedItemId?: string;
  }>({
    show: false,
    selectedItemId: "",
  });
  const isGeneralPricingModal = modalState.selectedItemId === "general";

  const defaultValues = {
    pricingValidFromDate: null,
    pricingGeneralSettings: undefined,
    pricingTesList: [],
  };

  const methods = useForm({ defaultValues, mode: "onChange" });

  const {
    handleSubmit,
    reset,
    watch,
    trigger,
    control,
    formState: { isDirty, errors, isSubmitting },
  } = methods;

  // Manually trigger validation
  useEffect(() => {
    trigger();
  }, [trigger]);

  const watchedValidFromDate = watch("pricingValidFromDate");
  const watchedPricingGeneralSettings = watch("pricingGeneralSettings");
  const watchedPricingTesList = watch("pricingTesList");

  const onSubmit = (formData: any) => {
    const updatedPricing = buildUpdatedPricingObjectForApi({
      contractId: contractData.id,
      contractVersion: contractData.version,
      validFrom: formatDateForBE(formData.pricingValidFromDate),
      pricingGeneralSettings: formData.pricingGeneralSettings,
      pricingTesList: formData.pricingTesList,
    });

    dispatch(updateContractPricing(updatedPricing)).then((action) => {
      if (isFulfilled(action)) closeModal();
    });
  };

  const onSave = () => {
    handleSubmit(onSubmit)();
  };

  const onCancel = () => {
    reset(defaultValues);
  };

  const handleClose = () => {
    onCancel();
    closeModal();
  };

  const onOpenModal = (selectedItemId?: string) =>
    setModalState({ show: true, selectedItemId });

  const onCloseModal = () => setModalState({ ...modalState, show: false });
  const onModalExited = () =>
    setModalState({ show: false, selectedItemId: "" });

  const tesPricingDataArray = buildTesPricingTableDataArray({
    pricingTesList: watchedPricingTesList,
    tesLocalizationIndex: tes,
  });

  const specialPricingDataArray = buildSpecialPricingTableDataArray({
    pricingGeneralSettings: watchedPricingGeneralSettings,
    label: t("contract.generalPricing"),
  });

  const getPricingPerTesData = () => {
    const foundData = watchedPricingTesList.find(
      (tesItem: any) => tesItem.tesId === modalState.selectedItemId,
    );
    return {
      pricingValidFromDate: formatDateForBE(watchedValidFromDate),
      pricingTesItem: foundData || {},
    };
  };

  const getPricingGeneralSettingsData = () => {
    return {
      pricingValidFromDate: formatDateForBE(watchedValidFromDate),
      pricingGeneralSettings: watchedPricingGeneralSettings,
    };
  };

  const areValuesValid = validatePricingGeneralSettings(
    watchedPricingGeneralSettings,
  );

  const saveDisabled =
    !isDirty ||
    !areValuesValid ||
    !isEmpty(errors) ||
    isSubmitting ||
    isLoading;

  return (
    <DialogContentWrapper
      title={t("contract.pricing")}
      margin
      onCancel={handleClose}
      onSave={onSave}
      saveDisabled={saveDisabled}
      childContainerSx={{ overflowY: "auto", overflowX: "hidden" }}
      customSaveButtonLabel={t("contract.savePricing")}
    >
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <OverlaySpinner isLoading={isLoading} />
          <VerticalAlignGrid container spacing={1}>
            <Grid
              container
              item
              xs={4}
              justifyContent="flex-end"
              paddingRight="0.5rem"
            >
              <Typography variant="secondary">
                {t("settings:effectiveFrom")}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Controller
                name="pricingValidFromDate"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t("common:requiredField"),
                  },
                  validate: {
                    validDate: (date) =>
                      (date && validateDate(date, "date")) ||
                      t("common:invalidDate") ||
                      "",
                  },
                }}
                render={({ field }) => (
                  <DesktopDatePicker
                    {...field}
                    value={field.value}
                    format="dd.MM.yyyy"
                    onChange={(newDate: Date | null) => field.onChange(newDate)}
                    slots={{ textField: TextFieldLight }}
                    slotProps={{
                      textField: {
                        sx: { width: "135px" },
                        error: !!errors.pricingValidFromDate,
                        helperText: errors.pricingValidFromDate?.message,
                        FormHelperTextProps: {
                          sx: {
                            position: "absolute",
                            top: "2.3rem",
                            width: "250px",
                          },
                        },
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </VerticalAlignGrid>
          <PricingTablesSection
            generalPricingDataArray={specialPricingDataArray}
            tesPricingDataArray={tesPricingDataArray}
            onOpenModal={onOpenModal}
          />
          <PricingEditModal
            isOpen={modalState.show}
            onClose={onCloseModal}
            openedFromPricingContent
            isGeneralPricingModal={isGeneralPricingModal}
            contractData={contractData}
            eezyCompanyName={eezyCompanyName}
            pricingPerTesData={getPricingPerTesData()}
            pricingGeneralSettingsData={getPricingGeneralSettingsData()}
            TransitionProps={{ onExited: onModalExited }}
          />
        </UnsavedChangesProvider>
      </FormProvider>
    </DialogContentWrapper>
  );
};

export default NewPricingNoExistingDataForm;
